import { useRef, useState, useEffect, useMemo } from 'react';
import { useDialog, useOverlay, useModal } from 'react-aria';
import { FocusScope } from 'react-aria';
import { CustomSelect } from '@shared/components/CustomSelect/CustomSelect';
import { CustomButton } from '@shared/components/CustomButton/CustomButton';
import type { SelectOption } from '@shared/components/CustomSelect/CustomSelect';
import { ManufacturerEpdData } from '@/shared/models/manufacturer-epd-data';
import {
  RequestMissingEpdModal,
  type RequestMissingEpdModalTranslations
} from '@shared/components/RequestMissingEpdModal/RequestMissingEpdModal';
import styles from './ProductEpdDataModal.module.css';
import clsx from 'clsx';

export interface ProductEpdDataModalTranslations {
  titles: {
    epd: string,
    cfp: string,
    generic: string,
  },
  subtitles: {
    cfp: string
  },
  emptyState: {
    epd: string,
    cfp: string,
    generic: string,
  },
  labels: {
    pickEpd: string,
    pickEpdVariant: string,
    pickCfp: string,
    missingEpd: string,
  },
  placeholders: {
    selectEpdInfoName: string,
    selectEpdDataLabel: string,
    select: string,
  },
  buttons: {
    cancel: string,
    save: string,
    saveEpd: string,
    saveCfp: string,
    saveGeneric: string,
    default: string,
    makeDefault: string,
  },
  aria: {
    closeModal: string,
    clearEpdInfoName: string,
    clearEpdDataLabel: string,
    removeSource: string,
  }
}

interface SelectedEpdData {
  source: string;
  id: string;
}

export interface ProductEpdDataModalProps {
  selectedEpdDataIds: string[];
  epdDataOptions: {
    [source: string]: SelectOption[];
  };
  epdRawData: ManufacturerEpdData[];
  sourceType: string;
  onClose: () => void;
  onSave: () => void;
  onUpdate: (selectedData: SelectedEpdData[], defaultSource: string) => void;
  onRequestMissingEpd?: (epdNameOrId: string) => void;
  translations: ProductEpdDataModalTranslations;
  requestMissingEpdTranslations: RequestMissingEpdModalTranslations;
}

export function ProductEpdDataModal({
  selectedEpdDataIds,
  epdDataOptions,
  epdRawData,
  sourceType,
  onClose,
  onSave,
  onUpdate,
  onRequestMissingEpd,
  translations,
  requestMissingEpdTranslations,
}: ProductEpdDataModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  const { overlayProps, underlayProps } = useOverlay(
    { isOpen: true, onClose, isDismissable: true },
    ref
  );
  const { modalProps } = useModal();
  const { dialogProps, titleProps } = useDialog({}, ref);

  const [selectedValues, setSelectedValues] = useState<{
    [source: string]: string;
  }>({});
  const [defaultSource, setDefaultSource] = useState<string>('');
  const [selectedEpdInfoName, setSelectedEpdInfoName] = useState<string>('');
  const [showMissingEpdModal, setShowMissingEpdModal] = useState<boolean>(false);

  const sources = Object.keys(epdDataOptions);
  const isEpdType = sourceType?.toUpperCase() === 'EPD';
  const isCfpType = sourceType?.toUpperCase() === 'CFP';
  const isGenericType = !isEpdType && !isCfpType;

  // Check if data is empty
  const isEpdDataEmpty = isEpdType && (!epdRawData || epdRawData.length === 0);
  const isCfpDataEmpty = isCfpType && (sources.length === 0 || sources.every(source => !epdDataOptions[source] || epdDataOptions[source].length === 0));
  const isGenericDataEmpty = isGenericType && (sources.length === 0 || sources.every(source => !epdDataOptions[source] || epdDataOptions[source].length === 0));
  const hasNoData = isEpdDataEmpty || isCfpDataEmpty || isGenericDataEmpty;

  // Helper function to build result array with default source first
  const buildResultArray = (values: { [source: string]: string }, defaultSrc: string): SelectedEpdData[] => {
    const result: SelectedEpdData[] = Object.entries(values)
      .filter(([_, id]) => id)
      .map(([source, id]) => ({ source, id }));

    // Put the default first if set
    if (defaultSrc) {
      const defaultIndex = result.findIndex((item) => item.source === defaultSrc);
      if (defaultIndex > 0) {
        const [defaultItem] = result.splice(defaultIndex, 1);
        result.unshift(defaultItem);
      }
    }

    return result;
  };

  // Initialize selected values from props
  useEffect(() => {
    // Skip if no options or raw data loaded yet
    if (Object.keys(epdDataOptions).length === 0) {
      return;
    }

    if (isEpdType && (!epdRawData || epdRawData.length === 0)) {
      return;
    }

    const initialValues: { [source: string]: string } = {};
    let defaultSourceFound = '';

    // For EPD type, we need to find the epdInfoName from raw data
    if (isEpdType && selectedEpdDataIds.length > 0) {
      const firstId = selectedEpdDataIds[0];
      const foundItem = epdRawData.find(item => item.epdDataId === firstId);
      if (foundItem) {
        setSelectedEpdInfoName(foundItem.epdInfoName || '');
        initialValues[sources[0]] = firstId;
        defaultSourceFound = sources[0];
      }
    } else {
      // For non-EPD types, map each ID to its source by checking epdDataOptions
      selectedEpdDataIds.forEach((id, index) => {
        Object.entries(epdDataOptions).forEach(([source, options]) => {
          const foundOption = options.find(opt => opt.value === id);
          if (foundOption) {
            initialValues[source] = id;
            // First ID is the default
            if (index === 0) {
              defaultSourceFound = source;
            }
          }
        });
      });
    }

    // Reorder to put default first
    const orderedValues: { [source: string]: string } = {};
    if (defaultSourceFound && initialValues[defaultSourceFound]) {
      orderedValues[defaultSourceFound] = initialValues[defaultSourceFound];
    }
    Object.entries(initialValues).forEach(([source, id]) => {
      if (source !== defaultSourceFound) {
        orderedValues[source] = id;
      }
    });

    setSelectedValues(orderedValues);
    setDefaultSource(defaultSourceFound);
  }, [selectedEpdDataIds, epdDataOptions, epdRawData, isEpdType]);

  // For EPD type, extract unique epdInfoNames and create cascading options
  const epdInfoNameOptions = useMemo(() => {
    if (!isEpdType || !epdRawData || epdRawData.length === 0) return [];

    // Extract unique epdInfoNames
    const uniqueNames = new Map<string, string>();
    epdRawData.forEach(item => {
      if (item.epdInfoName) {
        uniqueNames.set(item.epdInfoName, item.epdInfoName);
      }
    });

    return Array.from(uniqueNames.values())
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({
        value: name,
        label: name,
      }));
  }, [isEpdType, epdRawData]);

  const epdDataLabelOptions = useMemo(() => {
    if (!isEpdType || !selectedEpdInfoName || !epdRawData || epdRawData.length === 0) return [];

    // Filter raw data by selected epdInfoName and map to options
    return epdRawData
      .filter(item => item.epdInfoName === selectedEpdInfoName)
      .sort((a, b) => (a.epdDataLabel || '').localeCompare(b.epdDataLabel || ''))
      .map(item => ({
        value: item.epdDataId || '',
        label: item.epdDataLabel || '',
      }));
  }, [isEpdType, selectedEpdInfoName, epdRawData]);

  // Auto-select first EPD data when epdInfoName is selected and options become available
  useEffect(() => {
    if (isEpdType && selectedEpdInfoName && epdDataLabelOptions.length > 0) {
      const source = sources[0];
      const currentValue = selectedValues[source];

      // Only auto-select if no value is currently selected
      if (!currentValue) {
        const firstOption = epdDataLabelOptions[0];
        if (firstOption && firstOption.value) {
          handleSelectChange(source, firstOption.value);
        }
      }
    }
  }, [isEpdType, selectedEpdInfoName, epdDataLabelOptions]);

  const handleSelectChange = (source: string, value: string | string[]) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    const newValues = {
      ...selectedValues,
      [source]: newValue,
    };
    setSelectedValues(newValues);

    // For generic type: auto-select as default if no default is set and value is being added
    const currentDefault = defaultSource || (isGenericType && newValue ? source : '');
    if (isGenericType && newValue && !defaultSource) {
      setDefaultSource(source);
    }

    // Always emit update signal when selection changes
    const result = buildResultArray(newValues, currentDefault || defaultSource);
    onUpdate(result, currentDefault || defaultSource);
  };

  const handleToggleDefault = (source: string) => {
    // Don't allow unsetting if already default - just keep it
    if (defaultSource === source) {
      return;
    }

    const newDefault = source;
    setDefaultSource(newDefault);

    // Emit update signal immediately when default changes
    const result = buildResultArray(selectedValues, newDefault);
    onUpdate(result, newDefault);
  };

  const handleSave = () => {
    const result = buildResultArray(selectedValues, defaultSource);
    onUpdate(result, defaultSource);
    onSave();
    onClose();
  };

  if (!translations) return null

  return (
    <div className={styles.overlay} {...underlayProps}>
      <FocusScope contain restoreFocus autoFocus>
        <div
          {...overlayProps}
          {...dialogProps}
          {...modalProps}
          ref={ref}
          className={styles.modal}
        >
          <header className={styles.header}>
            <h2 {...titleProps} className={styles.title}>
              {isEpdType && translations.titles.epd}
              {isCfpType && translations.titles.cfp}
              {isGenericType && translations.titles.generic}
            </h2>
            {isCfpType && (
              <p className={styles.subtitle}>{translations.subtitles.cfp}</p>
            )}
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label={translations.aria.closeModal}
            >
              <span className="material-icons">close</span>
            </button>
          </header>

          <div className={clsx(styles.body, { [styles.isEpdType]: isEpdType })}>
            {hasNoData ? (
              <div className={styles.emptyState}>
                <div className={styles.icon}>
                  <span className="material-icons" style={{ fontSize: '48px' }}>inbox</span>
                </div>
                <div>
                  {isEpdType && translations.emptyState.epd}
                  {isCfpType && translations.emptyState.cfp}
                  {isGenericType && translations.emptyState.generic}
                </div>
              </div>
            ) : (
              <div className={styles.selectContainer}>
                {isEpdType ? (
                  // EPD Type: Show cascading selects for epdInfoName and epdDataLabel
                  <>
                    <div className={styles.missingEpdContainer}>
                      <button
                        type="button"
                        className={styles.missingEpdButton}
                        onClick={() => {
                          setShowMissingEpdModal(true);
                        }}
                      >
                        {translations.labels.missingEpd}
                      </button>
                    </div>
                    <div key={sources[0]} className={styles.selectRow}>
                      <div className={styles.selectWrapper}>
                        <CustomSelect
                          label={translations.labels.pickEpd}
                          options={epdInfoNameOptions}
                          value={selectedEpdInfoName}
                          onChange={(value) => {
                            const newValue = Array.isArray(value) ? value[0] : value;
                            setSelectedEpdInfoName(newValue);
                            // Reset data label selection when info name changes to allow auto-selection
                            const source = sources[0];
                            setSelectedValues({ [source]: '' });

                            // If cleared, also clear default source
                            if (!newValue) {
                              setDefaultSource('');
                            }
                          }}
                          placeholder={translations.placeholders.selectEpdInfoName}
                          size='medium'
                          fullWidth
                          hasNoRadius
                          borderColor="#bcd0d1"
                          hasNoBoxShadow
                          clearable
                          searchable
                          onClear={() => {
                            setSelectedEpdInfoName('');
                            const source = sources[0];
                            setSelectedValues({ [source]: '' });
                          }}
                        />
                      </div>
                    </div>
                    <div key={`${sources[0]}-label`} className={styles.selectRow}>
                      <div className={styles.selectWrapper}>
                        <CustomSelect
                          label={translations.labels.pickEpdVariant}
                          options={epdDataLabelOptions}
                          value={selectedValues[sources[0]] || ''}
                          onChange={(value) => {
                            handleSelectChange(sources[0], value);
                          }}
                          placeholder={translations.placeholders.selectEpdDataLabel}
                          size='medium'
                          fullWidth
                          disabled={!selectedEpdInfoName}
                          hasNoRadius
                          borderColor="#bcd0d1"
                          hasNoBoxShadow
                          clearable
                          onClear={() => handleSelectChange(sources[0], '')}
                        />
                      </div>
                    </div>
                  </>
                ) : isCfpType ? (
                  // CFP Type: Show single select without source label and default button
                  Object.keys(epdDataOptions).map((source) => {
                    const currentValue = selectedValues[source] || '';
                    return (
                      <div key={source} className={styles.selectRow}>
                        <div className={styles.selectWrapper}>
                          <CustomSelect
                            label={translations.labels.pickCfp}
                            options={epdDataOptions[source] || []}
                            value={currentValue}
                            onChange={(value) => {
                              handleSelectChange(source, value);
                            }}
                            placeholder={translations.placeholders.select}
                            size='medium'
                            fullWidth
                            hasNoRadius
                            borderColor="#bcd0d1"
                            hasNoBoxShadow
                            clearable
                            searchable
                            onClear={() => handleSelectChange(source, '')}
                          />
                        </div>
                      </div>
                    )
                  })
                ) : (
                  // Generic Type: Show source label and default button for multiple sources
                  Object.keys(epdDataOptions).map((source) => {
                    const currentValue = selectedValues[source] || '';
                    const isDefault = defaultSource === source;
                    const isDisabled = !selectedValues[source];

                    return (
                      <div key={source} className={styles.selectRow}>
                        <div className={styles.selectLabel}>{source}</div>
                        <div className={styles.selectWrapper}>
                          <CustomSelect
                            label=""
                            options={epdDataOptions[source] || []}
                            value={currentValue}
                            onChange={(value) => {
                              handleSelectChange(source, value);
                            }}
                            placeholder={translations.placeholders.select}
                            size='medium'
                            fullWidth
                            hasNoRadius
                            borderColor="#bcd0d1"
                            hasNoBoxShadow
                            clearable
                            onClear={() => handleSelectChange(source, '')}
                          />
                        </div>
                        <CustomButton
                          variant={isDefault ? 'primary' : 'outline'}
                          size="small"
                          onPress={() => handleToggleDefault(source)}
                          isDisabled={isDisabled}
                          className={styles.defaultButton}
                        >
                          {isDefault ? translations.buttons.default : translations.buttons.makeDefault}
                        </CustomButton>
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>

          <footer className={styles.footer}>
            <CustomButton
              variant="outline"
              onPress={onClose}
              size="small"
              style={{ borderColor: '#1a5765', color: '#1a5765' }}
            >
              {translations.buttons.cancel}
            </CustomButton>
            {!hasNoData && (
              <CustomButton
                variant="primary"
                onPress={handleSave}
                size="small"
                style={{ backgroundColor: '#1a5765', borderColor: '#1a5765' }}
              >
                {isEpdType ? translations.buttons.saveEpd : isCfpType ? translations.buttons.saveCfp : translations.buttons.saveGeneric}
              </CustomButton>
            )}
          </footer>
        </div>
      </FocusScope>
      {showMissingEpdModal && (
        <RequestMissingEpdModal
          onClose={() => setShowMissingEpdModal(false)}
          onSubmit={(epdNameOrId) => {
            if (onRequestMissingEpd) {
              onRequestMissingEpd(epdNameOrId);
            }
            setShowMissingEpdModal(false);
            onClose();
          }}
          translations={requestMissingEpdTranslations}
        />
      )}
    </div>
  );
}
